import { ReactPortal, RefObject, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

export type ViewportPoint = { x: number; y: number };

const PointAnchorDiv = styled.div`
  position: fixed;
  /* 1x1 rather than 0x0: Popover2025 only calls showPopover() once the anchor
     is visible (non-zero size), so a zero-size anchor would never open. */
  width: 1px;
  height: 1px;
  pointer-events: none;
`;

export type PointAnchor = {
  anchorRef: RefObject<HTMLDivElement | null>;
  anchorPortal: ReactPortal;
};

/**
 * Anchors a popover/menu to a viewport point instead of a DOM element — for
 * imperative "open at these coordinates" cases such as a canvas/graph
 * right-click. Returns the `anchorRef` to hand to Popover2025 (directly or via a
 * wrapper such as DropdownMenu2026) and the `anchorPortal` to render.
 *
 * The anchor is rendered into `document.body` so it stays on Popover2025's
 * direct anchor-name path, which reactively follows the point as it changes.
 * Inside an overflow-clipped ancestor Popover2025 falls back to a proxy that
 * snapshots the rect once, so the popover would not follow a moving point.
 */
export const usePointAnchor = (point: ViewportPoint): PointAnchor => {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const anchorPortal = createPortal(
    <PointAnchorDiv
      ref={anchorRef}
      style={{ top: point.y, left: point.x }}
      aria-hidden
    />,
    document.body
  );
  return { anchorRef, anchorPortal };
};
